'use client';

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import { Severity } from '@/components/ui/SeverityBadge';

interface RuleFormData {
  name: string;
  type: 'threshold' | 'signature' | 'behavioral' | 'anomaly';
  severity: Severity;
  condition: string;
  logSource: string;
  mitreId: string;
  mitreTactic: string;
  suppressionWindow: string;
  description: string;
  enabled: boolean;
}

interface RuleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingRule: any | null;
  onSave: (data: RuleFormData) => void;
}

export default function RuleFormModal({ isOpen, onClose, editingRule, onSave }: RuleFormModalProps) {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<RuleFormData>({
    defaultValues: {
      name: '',
      type: 'threshold',
      severity: 'medium',
      condition: '',
      logSource: '',
      mitreId: '',
      mitreTactic: '',
      suppressionWindow: '0',
      description: '',
      enabled: true,
    },
  });

  useEffect(() => {
    if (editingRule) {
      reset({
        name: editingRule.name,
        type: editingRule.type,
        severity: editingRule.severity,
        condition: editingRule.condition,
        logSource: editingRule.logSource,
        mitreId: editingRule.mitreId,
        mitreTactic: editingRule.mitreTactic,
        suppressionWindow: editingRule.suppressionWindow,
        description: editingRule.description || '',
        enabled: editingRule.enabled,
      });
    } else {
      reset();
    }
  }, [editingRule, reset, isOpen]);

  const onSubmit = async (data: RuleFormData) => {
    // Backend integration: POST /api/alert-rules or PATCH /api/alert-rules/:id
    await new Promise((r) => setTimeout(r, 400));
    onSave(data);
  };

  const inputClass = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 transition-colors";
  const labelClass = "block text-xs font-semibold text-zinc-400 mb-1.5";
  const errorClass = "mt-1 text-xs text-red-400";
  const helperClass = "mt-1 text-[11px] text-zinc-600";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingRule ? `Edit Rule — ${editingRule.id}` : 'Create Detection Rule'}
      subtitle={editingRule ? 'Modify rule parameters and conditions' : 'Define a new alert rule for threat detection'}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            type="submit"
            form="rule-form"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors active:scale-95 min-w-[120px] justify-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </>
            ) : editingRule ? 'Save Changes' : 'Create Rule'}
          </button>
        </>
      }
    >
      <form id="rule-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Basic Info */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-3 pb-2 border-b border-zinc-800">Basic Information</p>
          <div className="space-y-4">
            <div>
              <label htmlFor="rule-name" className={labelClass}>
                Rule Name <span className="text-red-400">*</span>
              </label>
              <input
                id="rule-name"
                type="text"
                placeholder="e.g. SSH Brute Force Detection"
                className={inputClass}
                {...register('name', { required: 'Rule name is required', minLength: { value: 5, message: 'Name must be at least 5 characters' } })}
              />
              {errors.name && <p className={errorClass}>{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="rule-type" className={labelClass}>
                  Rule Type <span className="text-red-400">*</span>
                </label>
                <select id="rule-type" className={inputClass} {...register('type', { required: true })}>
                  <option value="threshold">Threshold — count-based triggers</option>
                  <option value="signature">Signature — pattern matching</option>
                  <option value="behavioral">Behavioral — activity patterns</option>
                  <option value="anomaly">Anomaly — statistical deviation</option>
                </select>
                <p className={helperClass}>Determines how the condition is evaluated</p>
              </div>
              <div>
                <label htmlFor="rule-severity" className={labelClass}>
                  Severity <span className="text-red-400">*</span>
                </label>
                <select id="rule-severity" className={inputClass} {...register('severity', { required: true })}>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="info">Info</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="rule-description" className={labelClass}>Description</label>
              <textarea
                id="rule-description"
                rows={2}
                placeholder="Describe what this rule detects and why it matters..."
                className={`${inputClass} resize-none`}
                {...register('description')}
              />
            </div>
          </div>
        </div>

        {/* Detection Condition */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-3 pb-2 border-b border-zinc-800">Detection Condition</p>
          <div className="space-y-4">
            <div>
              <label htmlFor="rule-condition" className={labelClass}>
                Condition Expression <span className="text-red-400">*</span>
              </label>
              <textarea
                id="rule-condition"
                rows={3}
                placeholder="e.g. >50 failed SSH auths in 60s from same src IP"
                className={`${inputClass} font-mono text-xs resize-none`}
                {...register('condition', { required: 'Condition is required' })}
              />
              {errors.condition && <p className={errorClass}>{errors.condition.message}</p>}
              <p className={helperClass}>Human-readable condition. Use the rule type to determine evaluation logic.</p>
            </div>
            <div>
              <label htmlFor="rule-log-source" className={labelClass}>
                Log Source <span className="text-red-400">*</span>
              </label>
              <input
                id="rule-log-source"
                type="text"
                placeholder="e.g. syslog/auth, netflow/egress, waf/access"
                className={`${inputClass} font-mono text-xs`}
                {...register('logSource', { required: 'Log source is required' })}
              />
              {errors.logSource && <p className={errorClass}>{errors.logSource.message}</p>}
              <p className={helperClass}>Which log pipeline feeds this rule</p>
            </div>
          </div>
        </div>

        {/* MITRE ATT&CK Mapping */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-3 pb-2 border-b border-zinc-800">MITRE ATT&CK Mapping</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="rule-mitre-id" className={labelClass}>Technique ID</label>
              <input
                id="rule-mitre-id"
                type="text"
                placeholder="e.g. T1110"
                className={`${inputClass} font-mono`}
                {...register('mitreId', { pattern: { value: /^T\d{4}(\.\d{3})?$/, message: 'Format: T1234 or T1234.001' } })}
              />
              {errors.mitreId && <p className={errorClass}>{errors.mitreId.message}</p>}
            </div>
            <div>
              <label htmlFor="rule-mitre-tactic" className={labelClass}>Tactic</label>
              <select id="rule-mitre-tactic" className={inputClass} {...register('mitreTactic')}>
                <option value="">Select tactic...</option>
                {['Reconnaissance', 'Resource Development', 'Initial Access', 'Execution', 'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Credential Access', 'Discovery', 'Lateral Movement', 'Collection', 'Command & Control', 'Exfiltration', 'Impact'].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Suppression */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-3 pb-2 border-b border-zinc-800">Suppression & State</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="rule-suppression" className={labelClass}>Suppression Window</label>
              <select id="rule-suppression" className={inputClass} {...register('suppressionWindow')}>
                <option value="0">No suppression</option>
                <option value="1 min">1 minute</option>
                <option value="2 min">2 minutes</option>
                <option value="5 min">5 minutes</option>
                <option value="10 min">10 minutes</option>
                <option value="15 min">15 minutes</option>
                <option value="30 min">30 minutes</option>
                <option value="1 hour">1 hour</option>
              </select>
              <p className={helperClass}>Silence repeated alerts from same source after trigger</p>
            </div>
            <div className="flex flex-col justify-center">
              <label className={labelClass}>Rule State</label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-rose-500 focus:ring-rose-500/30" {...register('enabled')} />
                <span className="text-sm text-zinc-400">Enable rule immediately after saving</span>
              </label>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}